"use client";

import { useEffect, useState, type ReactNode } from "react";

/**
 * Progressive-enhancement wrapper for the server-rendered free report
 * sections (SEO build PHASE 2.1).
 *
 * Why this exists: `/stocks/[exchange]/[ticker]` renders the client
 * `ReportViewer` for the full interactive UX (votes, paywall, PDF, TOC).
 * That component fetches the report client-side, so the initial HTML used
 * to contain ~nav-only text — invisible to Google's first wave and to
 * AI-citation crawlers (ChatGPT-User, Claude-User, PerplexityBot) that do
 * not execute JS. The fix is a server-rendered block with the free report
 * text — but rendering it permanently alongside the client viewer would
 * visibly duplicate the free sections.
 *
 * Resolution: children (the server block) are part of the initial server
 * HTML — crawlers and no-JS user agents get the full free-section text.
 * Once React hydrates in a real browser, this wrapper unmounts the block
 * and the interactive `ReportViewer` is the single source of visible
 * content, exactly as today. The first client render returns the children
 * (matching the server output) so there is no hydration mismatch; the
 * unmount happens in a post-hydration effect.
 */
export function HideAfterHydration({ children }: { children: ReactNode }) {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  if (hydrated) return null;
  return <>{children}</>;
}
